import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { getUserPool } from "../services/api/apiClient";
import { setAxiosUserEmail } from "../services/api/axiosInstance";
import { LoginFlow } from "../services/api/types/auth/userPool";
import { User } from "../services/api/types/general/user";

type LoadStatus = "idle" | "loading" | "loaded" | "error";

type State = {
  user: User | null;
  loadStatus: LoadStatus;
};

type Action =
  | { type: "SET_USER"; payload: User }
  | { type: "CLEAR_USER" }
  | { type: "REMOVE_LOADER" }
  | {
      type: "SET_LOAD_STATUS";
      payload: LoadStatus;
    };

const initialState: State = {
  user: null,
  loadStatus: "loading",
};

const AuthContext = createContext<{
  auth: State;
  dispatch: React.Dispatch<Action>;
}>({
  auth: initialState,
  dispatch: () => null,
});

const userReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SET_USER":
      return { ...state, user: action.payload, loadStatus: "loaded" };
    case "CLEAR_USER":
      setAxiosUserEmail(null);
      return { ...state, user: null, loadStatus: "loaded" };
    case "REMOVE_LOADER":
      return { ...state, loadStatus: "loaded" };
    case "SET_LOAD_STATUS": {
      return {
        ...state,
        loadStatus: action.payload,
      };
    }
    default:
      return state;
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [auth, dispatch] = useReducer(userReducer, initialState);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const username = localStorage.getItem("userName");
        const rememberMe = localStorage.getItem("rememberMe");
        if (rememberMe && JSON.parse(rememberMe) && username) {
          const userPoolResponse = await getUserPool({
            email: username,
            loginFlow: LoginFlow.LOGIN,
            portalUrl: window.location.origin,
          });
          setAxiosUserEmail(userPoolResponse.cognitoUser);
        }
      } catch (error) {
        console.log("Error: ", error);
        localStorage.removeItem("userName");
        localStorage.removeItem("rememberMe");
      } finally {
        dispatch({ type: "SET_LOAD_STATUS", payload: "loaded" });
      }
    };

    fetchUser();
  }, []);

  const memoizedValue = useMemo(() => ({ auth, dispatch }), [auth, dispatch]);

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
